import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Layout } from '../../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { Users, Plus, Edit, Trash2, ArrowLeft, Eye, LayoutGrid, List } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const TeamsSettings = () => {
  const navigate = useNavigate();
  const [teams, setTeams] = useState([]);
  const [markets, setMarkets] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMarketId, setSelectedMarketId] = useState('all');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [marketDialogOpen, setMarketDialogOpen] = useState(false);
  const [manageMarketsDialogOpen, setManageMarketsDialogOpen] = useState(false);
  const [viewTeamDialogOpen, setViewTeamDialogOpen] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [membersViewMode, setMembersViewMode] = useState('cards');
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    market_id: '',
    department: '',
    manager_id: '',
    assistant_manager_id: '',
    crew_labor_percent: 0
  });
  const [users, setUsers] = useState([]);
  const [marketFormData, setMarketFormData] = useState({
    name: '',
    location: '',
    is_active: true
  });

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    fetchTeams();
  }, [selectedMarketId]);

  const fetchData = async () => {
    try {
      const [teamsRes, marketsRes, usersRes, departmentsRes] = await Promise.all([
        axios.get(`${API}/teams`),
        axios.get(`${API}/markets`),
        axios.get(`${API}/users`),
        axios.get(`${API}/departments`)
      ]);
      setTeams(teamsRes.data);
      setMarkets(marketsRes.data);
      setUsers(usersRes.data);
      setDepartments(departmentsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load teams and markets');
    } finally {
      setLoading(false);
    }
  };

  const fetchTeams = async () => {
    try {
      const url = selectedMarketId === 'all' ? `${API}/teams` : `${API}/teams?market_id=${selectedMarketId}`;
      const response = await axios.get(url);
      setTeams(response.data);
    } catch (error) {
      console.error('Failed to fetch teams:', error);
      toast.error('Failed to load teams');
    }
  };

  const handleCreateMarket = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/markets`, marketFormData);
      toast.success('Market created successfully');
      setMarketDialogOpen(false);
      setMarketFormData({ name: '', location: '', is_active: true });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create market');
    }
  };

  const handleCreateTeam = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/teams`, formData);
      toast.success('Team created successfully');
      setCreateDialogOpen(false);
      setFormData({ name: '', description: '', market_id: '', department: '', manager_id: '', assistant_manager_id: '', crew_labor_percent: 0 });
      fetchTeams();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create team');
    }
  };

  const handleUpdateTeam = async (e) => {
    e.preventDefault();
    try {
      await axios.patch(`${API}/teams/${selectedTeam.id}`, formData);
      toast.success('Team updated successfully');
      setEditDialogOpen(false);
      setSelectedTeam(null);
      fetchTeams();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update team');
    }
  };

  const handleDeleteTeam = async (teamId) => {
    if (!window.confirm('Are you sure you want to delete this team?')) return;
    
    try {
      await axios.delete(`${API}/teams/${teamId}`);
      toast.success('Team deleted successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete team');
    }
  };

  const handleDeleteMarket = async (marketId) => {
    if (!window.confirm('Are you sure you want to delete this market? Teams assigned to this market will need to be reassigned.')) return;
    
    try {
      await axios.delete(`${API}/markets/${marketId}`);
      toast.success('Market deleted successfully');
      setSelectedMarketId('all');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete market');
    }
  };

  const openEditDialog = (team) => {
    setSelectedTeam(team);
    setFormData({
      name: team.name,
      description: team.description || '',
      market_id: team.market_id || '',
      department: team.department || '',
      manager_id: team.manager_id || '',
      assistant_manager_id: team.assistant_manager_id || '',
      crew_labor_percent: team.crew_labor_percent || 0
    });
    setEditDialogOpen(true);
  };

  const openViewTeamDialog = async (team) => {
    setSelectedTeam(team);
    setViewTeamDialogOpen(true);
    
    // Fetch team members
    try {
      // Filter users who have this team in their team_ids array
      const members = users.filter(user => 
        user.team_ids && user.team_ids.includes(team.id)
      );
      setTeamMembers(members);
    } catch (error) {
      console.error('Failed to fetch team members:', error);
      toast.error('Failed to load team members');
      setTeamMembers([]);
    }
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Button variant="ghost" onClick={() => navigate('/settings')}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-3xl font-bold">Teams Management</h1>
              <p className="text-muted-foreground">Create and manage teams across your organization</p>
            </div>
          </div>
        </div>

        {/* Market Selector and Actions */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between gap-4">
              <div className="flex-1">
                <Label>Filter by Market</Label>
                <Select value={selectedMarketId} onValueChange={setSelectedMarketId}>
                  <SelectTrigger className="max-w-md">
                    <SelectValue placeholder="Select market" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Markets</SelectItem>
                    {markets.map(market => (
                      <SelectItem key={market.id} value={market.id}>{market.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-2">
                <Dialog open={marketDialogOpen} onOpenChange={setMarketDialogOpen}>
                  <DialogTrigger asChild>
                    <Button variant="outline">
                      <Plus className="h-4 w-4 mr-2" />
                      Add Market
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Create New Market</DialogTitle>
                      <DialogDescription>Add a new market location</DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleCreateMarket} className="space-y-4">
                      <div>
                        <Label>Market Name *</Label>
                        <Input
                          value={marketFormData.name}
                          onChange={(e) => setMarketFormData({...marketFormData, name: e.target.value})}
                          placeholder="e.g., Tampa Bay, Orlando"
                          required
                        />
                      </div>
                      <div>
                        <Label>Location</Label>
                        <Input
                          value={marketFormData.location}
                          onChange={(e) => setMarketFormData({...marketFormData, location: e.target.value})}
                          placeholder="City, State"
                        />
                      </div>
                      <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => setMarketDialogOpen(false)}>Cancel</Button>
                        <Button type="submit">Create Market</Button>
                      </DialogFooter>
                    </form>
                  </DialogContent>
                </Dialog>

                <Dialog open={manageMarketsDialogOpen} onOpenChange={setManageMarketsDialogOpen}>
                  <DialogTrigger asChild>
                    <Button variant="outline">
                      <Edit className="h-4 w-4 mr-2" />
                      Manage Markets
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl">
                    <DialogHeader>
                      <DialogTitle>Manage Markets</DialogTitle>
                      <DialogDescription>View and manage all market locations</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3 max-h-96 overflow-y-auto">
                      {markets.length === 0 ? (
                        <p className="text-center text-muted-foreground py-8">No markets created yet</p>
                      ) : (
                        markets.map(market => (
                          <div key={market.id} className="flex items-center justify-between p-4 border rounded-lg">
                            <div className="flex-1">
                              <h4 className="font-semibold">{market.name}</h4>
                              {market.location && (
                                <p className="text-sm text-muted-foreground">{market.location}</p>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              {market.is_active ? (
                                <Badge className="bg-green-600">Active</Badge>
                              ) : (
                                <Badge variant="outline">Inactive</Badge>
                              )}
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteMarket(market.id)}
                              >
                                <Trash2 className="h-4 w-4 text-red-600" />
                              </Button>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end">
          <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="create-team-button">
                <Plus className="h-4 w-4 mr-2" />
                Create Team
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create New Team</DialogTitle>
                <DialogDescription>Add a new team to your organization</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateTeam} className="space-y-4">
                <div>
                  <Label>Team Name *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="e.g., Sales Team Florida"
                    required
                  />
                </div>
                <div>
                  <Label>Description</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    placeholder="Brief description of the team's purpose"
                    rows={3}
                  />
                </div>
                <div>
                  <Label>Market</Label>
                  <Select value={formData.market_id} onValueChange={(value) => setFormData({...formData, market_id: value})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select market" />
                    </SelectTrigger>
                    <SelectContent>
                      {markets.map(market => (
                        <SelectItem key={market.id} value={market.id}>{market.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Department</Label>
                  <Select 
                    value={formData.department || 'none'} 
                    onValueChange={(value) => setFormData({...formData, department: value === 'none' ? '' : value})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select department" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No Department</SelectItem>
                      {departments.map(dept => (
                        <SelectItem key={dept.id || dept.name} value={dept.name}>
                          {dept.display_name || dept.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Manager</Label>
                  <Select value={formData.manager_id || 'none'} onValueChange={(value) => setFormData({...formData, manager_id: value === 'none' ? '' : value})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select manager" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {users.map(user => (
                        <SelectItem key={user.id} value={user.id}>{user.first_name} {user.last_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Assistant Manager</Label>
                  <Select value={formData.assistant_manager_id || 'none'} onValueChange={(value) => setFormData({...formData, assistant_manager_id: value === 'none' ? '' : value})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select assistant manager" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {users.map(user => (
                        <SelectItem key={user.id} value={user.id}>{user.first_name} {user.last_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Crew Labor % (of Gross Contract)</Label>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    step="0.5"
                    value={formData.crew_labor_percent}
                    onChange={(e) => setFormData({...formData, crew_labor_percent: parseFloat(e.target.value) || 0})}
                    placeholder="0"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Percentage of gross contract paid to crew.
                  </p>
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
                  <Button type="submit">Create Team</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
            <p className="mt-4 text-muted-foreground">Loading teams...</p>
          </div>
        ) : teams.length === 0 ? (
          <Card>
            <CardContent className="text-center py-12">
              <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">No teams yet</h3>
              <p className="text-muted-foreground mb-4">Create your first team to get started</p>
              <Button onClick={() => setCreateDialogOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Create Team
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {teams.map(team => (
              <Card key={team.id} className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <CardTitle className="text-xl">{team.name}</CardTitle>
                      <CardDescription className="mt-2">
                        {team.description || 'No description'}
                      </CardDescription>
                    </div>
                    <Badge variant="secondary">{team.member_count} members</Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 mb-4">
                    {team.market_name && (
                      <div className="text-sm">
                        <span className="text-muted-foreground">Market:</span>
                        <span className="ml-2 font-medium">{team.market_name}</span>
                      </div>
                    )}
                    {team.department && (
                      <div className="text-sm">
                        <span className="text-muted-foreground">Department:</span>
                        <span className="ml-2 font-medium">{team.department}</span>
                      </div>
                    )}
                    {team.manager_name && (
                      <div className="text-sm">
                        <span className="text-muted-foreground">Manager:</span>
                        <span className="ml-2 font-medium">{team.manager_name}</span>
                      </div>
                    )}
                    {team.assistant_manager_name && (
                      <div className="text-sm">
                        <span className="text-muted-foreground">Asst. Manager:</span>
                        <span className="ml-2 font-medium">{team.assistant_manager_name}</span>
                      </div>
                    )}
                    {team.crew_labor_percent > 0 && (
                      <div className="text-sm">
                        <span className="text-muted-foreground">Crew Labor %:</span>
                        <span className="ml-2 font-medium text-green-600">{team.crew_labor_percent}%</span>
                      </div>
                    )}
                  </div>
                  <div className="flex space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1"
                      onClick={() => openViewTeamDialog(team)}
                    >
                      <Eye className="h-4 w-4 mr-2" />
                      View Team
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openEditDialog(team)}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDeleteTeam(team.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Edit Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit Team</DialogTitle>
              <DialogDescription>Update team information</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleUpdateTeam} className="space-y-4">
              <div>
                <Label>Team Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required
                />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  rows={3}
                />
              </div>
              <div>
                <Label>Market</Label>
                <Select value={formData.market_id} onValueChange={(value) => setFormData({...formData, market_id: value})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select market" />
                  </SelectTrigger>
                  <SelectContent>
                    {markets.map(market => (
                      <SelectItem key={market.id} value={market.id}>{market.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Department</Label>
                <Select 
                  value={formData.department || 'none'} 
                  onValueChange={(value) => setFormData({...formData, department: value === 'none' ? '' : value})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select department" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Department</SelectItem>
                    {departments.map(dept => (
                      <SelectItem key={dept.id || dept.name} value={dept.name}>
                        {dept.display_name || dept.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Manager</Label>
                <Select value={formData.manager_id || 'none'} onValueChange={(value) => setFormData({...formData, manager_id: value === 'none' ? '' : value})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select manager" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {users.map(user => (
                      <SelectItem key={user.id} value={user.id}>{user.first_name} {user.last_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Assistant Manager</Label>
                <Select value={formData.assistant_manager_id || 'none'} onValueChange={(value) => setFormData({...formData, assistant_manager_id: value === 'none' ? '' : value})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select assistant manager" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {users.map(user => (
                      <SelectItem key={user.id} value={user.id}>{user.first_name} {user.last_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Crew Labor % (of Gross Contract)</Label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  step="0.5"
                  value={formData.crew_labor_percent}
                  onChange={(e) => setFormData({...formData, crew_labor_percent: parseFloat(e.target.value) || 0})}
                  placeholder="0"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Percentage of gross contract paid to crew. Used in estimate financial calculations.
                </p>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>Cancel</Button>
                <Button type="submit">Update Team</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* View Team Members Dialog */}
        <Dialog open={viewTeamDialogOpen} onOpenChange={setViewTeamDialogOpen}>
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
            <DialogHeader>
              <div className="flex items-center justify-between">
                <div>
                  <DialogTitle>{selectedTeam?.name}</DialogTitle>
                  <DialogDescription>
                    {teamMembers.length} {teamMembers.length === 1 ? 'member' : 'members'}
                  </DialogDescription>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant={membersViewMode === 'cards' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setMembersViewMode('cards')}
                  >
                    <LayoutGrid className="h-4 w-4" />
                  </Button>
                  <Button
                    variant={membersViewMode === 'list' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setMembersViewMode('list')}
                  >
                    <List className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </DialogHeader>

            <div className="flex-1 overflow-y-auto">
              {teamMembers.length === 0 ? (
                <div className="text-center py-12">
                  <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">No members assigned to this team yet</p>
                </div>
              ) : membersViewMode === 'cards' ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-1">
                  {teamMembers.map(member => (
                    <Card key={member.id} className="hover:shadow-md transition-shadow">
                      <CardHeader className="pb-3">
                        <div className="flex items-start justify-between">
                          <div>
                            <CardTitle className="text-base">
                              {member.first_name} {member.last_name}
                            </CardTitle>
                            <CardDescription className="text-sm">
                              {member.email}
                            </CardDescription>
                          </div>
                          <Badge variant={member.is_active ? 'default' : 'secondary'} className="text-xs">
                            {member.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                        </div>
                      </CardHeader>
                      <CardContent className="pt-0">
                        <div className="space-y-1">
                          <div className="text-sm">
                            <span className="text-muted-foreground">Role:</span>
                            <span className="ml-2 font-medium capitalize">{member.role.replace('_', ' ')}</span>
                          </div>
                          {member.department && (
                            <div className="text-sm">
                              <span className="text-muted-foreground">Department:</span>
                              <span className="ml-2 font-medium">{member.department}</span>
                            </div>
                          )}
                          {member.phone && (
                            <div className="text-sm">
                              <span className="text-muted-foreground">Phone:</span>
                              <span className="ml-2 font-medium">{member.phone}</span>
                            </div>
                          )}
                          {member.team_ids && member.team_ids.length > 0 && (
                            <div className="text-sm">
                              <span className="text-muted-foreground">Teams:</span>
                              <span className="ml-2 font-medium">{member.team_ids.length}</span>
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <div className="border rounded-lg">
                  <table className="w-full">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="text-left p-3 text-sm font-medium">Name</th>
                        <th className="text-left p-3 text-sm font-medium">Email</th>
                        <th className="text-left p-3 text-sm font-medium">Role</th>
                        <th className="text-left p-3 text-sm font-medium">Department</th>
                        <th className="text-left p-3 text-sm font-medium">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {teamMembers.map(member => (
                        <tr key={member.id} className="border-t hover:bg-muted/30">
                          <td className="p-3 text-sm font-medium">
                            {member.first_name} {member.last_name}
                          </td>
                          <td className="p-3 text-sm text-muted-foreground">
                            {member.email}
                          </td>
                          <td className="p-3 text-sm">
                            <Badge variant="outline" className="capitalize">
                              {member.role.replace('_', ' ')}
                            </Badge>
                          </td>
                          <td className="p-3 text-sm">
                            {member.department || '-'}
                          </td>
                          <td className="p-3 text-sm">
                            <Badge 
                              variant={member.is_active ? 'default' : 'secondary'}
                              className={member.is_active ? 'bg-green-600' : ''}
                            >
                              {member.is_active ? 'Active' : 'Inactive'}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <DialogFooter className="mt-4">
              <Button variant="outline" onClick={() => setViewTeamDialogOpen(false)}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};